import {
    BadgeCategory,
    BadgeTier,
    type BadgeAggregateCounts,
    type BadgeDefinition,
    type DieSides,
    type ProgressionBadgeDefinition,
    type ProgressionBadgeVariant,
    type SpecialBadgeDefinition,
} from '../models/Badge';
import { Gender } from '../models/User';
import { pluralizeCroatian } from './croatianPlural';
import { applyGender, pickGender } from './genderedText';

// The thresholds and titles here are the source of truth for client-side
// rendering. The slice-2 SQL function recompute_badges_for_user mirrors these
// exact values — keep them in sync or auto-badges will diverge from what the
// UI promises.
const GAME_TIERS = [
    { tier: BadgeTier.Wood, threshold: 1 },
    { tier: BadgeTier.Bronze, threshold: 5 },
    { tier: BadgeTier.Silver, threshold: 20 },
    { tier: BadgeTier.Gold, threshold: 50 },
] as const;

const SYSTEM_TIERS = [
    { tier: BadgeTier.Wood, threshold: 1 },
    { tier: BadgeTier.Bronze, threshold: 3 },
    { tier: BadgeTier.Silver, threshold: 7 },
    { tier: BadgeTier.Gold, threshold: 15 },
] as const;

// Repeated games skips the wood tier on purpose — the floor of "ran the same
// game twice" is already a meaningful achievement and a wood-coloured chip at
// 2 would crowd the GM strip without adding signal.
const REPEATED_GAMES_TIERS = [
    { tier: BadgeTier.Bronze, threshold: 2 },
    { tier: BadgeTier.Silver, threshold: 3 },
    { tier: BadgeTier.Gold, threshold: 6 },
] as const;

// Streak floor is 2 — a single attended event isn't a streak. Nothing is
// rendered for `longest < 2`, so wood at 2 is the lowest visible chip.
const STREAK_TIERS = [
    { tier: BadgeTier.Wood, threshold: 2 },
    { tier: BadgeTier.Bronze, threshold: 3 },
    { tier: BadgeTier.Silver, threshold: 5 },
    { tier: BadgeTier.Gold, threshold: 10 },
] as const;

// Mentors (distinct GMs played under) — 2/3/7/15. Wood at 2 (not 1) because
// you've already played a counted game under a GM by definition once you have
// any counted games at all; the player-track chip covers the "first one" case.
const MENTORS_TIERS = [
    { tier: BadgeTier.Wood, threshold: 2 },
    { tier: BadgeTier.Bronze, threshold: 3 },
    { tier: BadgeTier.Silver, threshold: 7 },
    { tier: BadgeTier.Gold, threshold: 15 },
] as const;

// Unique players GM'd — 8/16/40/80 (≈ 2/4/10/20 full tables of 4). Tougher
// floor than other tracks because each counted game contributes up to ~4
// uniques but with heavy group-overlap reality. Hitting wood means you've
// drawn beyond your home group; gold (~20 tables of distinct players) sits
// above the GM Gold (50 games) threshold deliberately.
const UNIQUE_PLAYERS_GMED_TIERS = [
    { tier: BadgeTier.Wood, threshold: 8 },
    { tier: BadgeTier.Bronze, threshold: 16 },
    { tier: BadgeTier.Silver, threshold: 40 },
    { tier: BadgeTier.Gold, threshold: 80 },
] as const;

type GenderedTitle = { m: string; f?: string };

const PLAYER_TITLES: Record<BadgeTier, GenderedTitle> = {
    [BadgeTier.Wood]: { m: 'Pripravnik', f: 'Pripravnica' },
    [BadgeTier.Bronze]: { m: 'Pustolov', f: 'Pustolovka' },
    [BadgeTier.Silver]: { m: 'Veteran', f: 'Veteranka' },
    [BadgeTier.Gold]: { m: 'Junak', f: 'Junakinja' },
};

const GM_TITLES: Record<BadgeTier, GenderedTitle> = {
    [BadgeTier.Wood]: { m: 'Šegrt', f: 'Šegrtkinja' },
    [BadgeTier.Bronze]: { m: 'Vještac', f: 'Vještica' },
    [BadgeTier.Silver]: { m: 'Čarobnjak', f: 'Čarobnica' },
    // "Arhimag" reads gender-neutral in Croatian; no idiomatic feminine.
    [BadgeTier.Gold]: { m: 'Arhimag' },
};

const SYSTEMS_PLAYED_TITLES: Record<BadgeTier, GenderedTitle> = {
    // "Polihistor" doesn't have widely-used feminine forms.
    [BadgeTier.Wood]: { m: 'Radoznalac', f: 'Radoznalka' },
    [BadgeTier.Bronze]: { m: 'Putnik', f: 'Putnica' },
    [BadgeTier.Silver]: { m: 'Istraživač', f: 'Istraživačica' },
    [BadgeTier.Gold]: { m: 'Polihistor' },
};

const SYSTEMS_GMED_TITLES: Record<BadgeTier, GenderedTitle> = {
    [BadgeTier.Wood]: { m: 'Tragač', f: 'Tragačica' },
    [BadgeTier.Bronze]: { m: 'Vodič', f: 'Vodičica' },
    // "Polimat" / "Demiurg" read neutral here — no feminine override.
    [BadgeTier.Silver]: { m: 'Polimat' },
    [BadgeTier.Gold]: { m: 'Demiurg' },
};

// Repeated games never awards wood; the entry left here is unreachable but
// satisfies the Record type so the shared track builder can be reused.
const REPEATED_GAMES_GMED_TITLES: Record<BadgeTier, GenderedTitle> = {
    [BadgeTier.Wood]: { m: '' },
    [BadgeTier.Bronze]: { m: 'Ponavljač', f: 'Ponavljačica' },
    // "Hit Gejm" / "Smor" are slang noun phrases; no gendered form.
    [BadgeTier.Silver]: { m: 'Hit Gejm' },
    [BadgeTier.Gold]: { m: 'Smor' },
};

// Streak titles are adjectives that decline by gender in Croatian.
// "Konstanta" is already feminine-noun shaped; "Sajko" is slang and neutral.
const STREAK_TITLES: Record<BadgeTier, GenderedTitle> = {
    [BadgeTier.Wood]: { m: 'Uporan', f: 'Uporna' },
    [BadgeTier.Bronze]: { m: 'Postojan', f: 'Postojana' },
    [BadgeTier.Silver]: { m: 'Konstanta' },
    [BadgeTier.Gold]: { m: 'Sajko' },
};

const MENTORS_TITLES: Record<BadgeTier, GenderedTitle> = {
    [BadgeTier.Wood]: { m: 'Učenik', f: 'Učenica' },
    // "Lutalica" / "Eklektik" don't have a cleaner feminine in common use.
    [BadgeTier.Bronze]: { m: 'Lutalica' },
    [BadgeTier.Silver]: { m: 'Hodočasnik', f: 'Hodočasnica' },
    [BadgeTier.Gold]: { m: 'Eklektik' },
};

// Unique players GM'd: the GM's reach arc. "Idol" reads gender-neutral and
// keeps the top tier secular — deliberately picked over Mesija to avoid
// religious overtones in a mixed community.
const UNIQUE_PLAYERS_GMED_TITLES: Record<BadgeTier, GenderedTitle> = {
    [BadgeTier.Wood]: { m: 'Domaćin', f: 'Domaćica' },
    [BadgeTier.Bronze]: { m: 'Propovjednik', f: 'Propovjednica' },
    [BadgeTier.Silver]: { m: 'Prorok', f: 'Proročica' },
    [BadgeTier.Gold]: { m: 'Idol' },
};

function buildProgressionTrack(
    category: Exclude<BadgeCategory, 'special'>,
    dieSides: DieSides | null,
    titles: Record<BadgeTier, GenderedTitle>,
    tiers: ReadonlyArray<{ tier: BadgeTier; threshold: number }>,
    descriptionFor: (tier: BadgeTier, threshold: number) => string,
    variant?: ProgressionBadgeVariant,
): ProgressionBadgeDefinition[] {
    return tiers.map(({ tier, threshold }) => ({
        key: `${category}.${tier}`,
        category,
        tier,
        ...(dieSides != null ? { dieSides } : {}),
        title: titles[tier].m,
        ...(titles[tier].f != null ? { titleFemale: titles[tier].f } : {}),
        description: descriptionFor(tier, threshold),
        threshold,
        ...(variant ? { variant } : {}),
    }));
}

/** Forms for [1, 2-4, 5+] following Croatian plural rules. */
const PROGRESSION_NOUN_FORMS: Record<Exclude<BadgeCategory, 'special'>, [string, string, string]> = {
    [BadgeCategory.GM]: ['igra', 'igre', 'igara'],
    [BadgeCategory.Player]: ['igra', 'igre', 'igara'],
    [BadgeCategory.SystemsGmed]: ['sustav', 'sustava', 'sustava'],
    [BadgeCategory.SystemsPlayed]: ['sustav', 'sustava', 'sustava'],
    [BadgeCategory.RepeatedGamesGmed]: ['put', 'puta', 'puta'],
    [BadgeCategory.Streak]: ['uzastopan event', 'uzastopna eventa', 'uzastopnih evenata'],
    [BadgeCategory.Mentors]: ['GM', 'GMa', 'GMova'],
    [BadgeCategory.UniquePlayersGmed]: ['različit igrač', 'različita igrača', 'različitih igrača'],
};

/**
 * Returns a formatted Croatian noun phrase like "5 igara" or "1 sustav" for a
 * progression category. Returns null for `special` (which has no count semantics).
 */
export function formatProgressionAmount(category: BadgeCategory, count: number): string | null {
    if (category === BadgeCategory.Special) {
        return null;
    }

    const forms = PROGRESSION_NOUN_FORMS[category];
    return `${count} ${pluralizeCroatian(count, forms)}`;
}

// "različit" declines with the noun, so the adjective form has to track the
// number's paucal/many bucket — "3 različita sustava" vs "7 različitih sustava".
// The chip-side noun phrases in PROGRESSION_NOUN_FORMS don't need the adjective;
// these are only consumed by the description templates below.
const SYSTEM_ADJ_FORMS: [string, string, string] = [
    'različit sustav',
    'različita sustava',
    'različitih sustava',
];

const MENTORS_ADJ_FORMS: [string, string, string] = [
    'različitim GMom',
    'različita GMa',
    'različitih GMova',
];

const playerBadges = buildProgressionTrack(
    BadgeCategory.Player,
    6,
    PLAYER_TITLES,
    GAME_TIERS,
    (_tier, threshold) =>
        threshold === 1
            ? 'Odigra(o|la) prvu kampanju ili one-shot.'
            : `Odigra(o|la) barem ${threshold} igara.`,
);

const gmBadges = buildProgressionTrack(
    BadgeCategory.GM,
    20,
    GM_TITLES,
    GAME_TIERS,
    (_tier, threshold) =>
        threshold === 1
            ? 'Vod(io|ila) prvu sesiju za stolom.'
            : `Vod(io|ila) barem ${threshold} igara.`,
);

const systemsPlayedBadges = buildProgressionTrack(
    BadgeCategory.SystemsPlayed,
    8,
    SYSTEMS_PLAYED_TITLES,
    SYSTEM_TIERS,
    (_tier, threshold) =>
        threshold === 1
            ? 'Iskus(io|ila) prvi sustav.'
            : `Igra(o|la) barem ${threshold} ${pluralizeCroatian(threshold, SYSTEM_ADJ_FORMS)}.`,
);

const systemsGmedBadges = buildProgressionTrack(
    BadgeCategory.SystemsGmed,
    12,
    SYSTEMS_GMED_TITLES,
    SYSTEM_TIERS,
    (_tier, threshold) =>
        threshold === 1
            ? 'Vod(io|ila) prvi sustav.'
            : `Vod(io|ila) barem ${threshold} ${pluralizeCroatian(threshold, SYSTEM_ADJ_FORMS)}.`,
);

const repeatedGamesGmedBadges = buildProgressionTrack(
    BadgeCategory.RepeatedGamesGmed,
    6,
    REPEATED_GAMES_GMED_TITLES,
    REPEATED_GAMES_TIERS,
    (_tier, threshold) =>
        `Vod(io|ila) istu igru barem ${threshold} puta.`,
    '2d6',
);

// Streak: longest historical run of consecutive concluded events attended.
// No die — uses the flame variant. Wood at 2 is the floor.
const streakBadges = buildProgressionTrack(
    BadgeCategory.Streak,
    null,
    STREAK_TITLES,
    STREAK_TIERS,
    (_tier, threshold) =>
        // Thresholds straddle the paucal/many boundary (2, 3 → "uzastopna
        // eventa"; 5, 10 → "uzastopnih evenata"), so the noun phrase has to
        // be plural-aware rather than baked into the template.
        `Posjet(io|ila) barem ${formatProgressionAmount(BadgeCategory.Streak, threshold) ?? threshold}.`,
    'flame',
);

// Mentors: distinct GMs the user has played counted games under. d4.
const mentorsBadges = buildProgressionTrack(
    BadgeCategory.Mentors,
    4,
    MENTORS_TITLES,
    MENTORS_TIERS,
    (_tier, threshold) =>
        `Igra(o|la) pod barem ${threshold} ${pluralizeCroatian(threshold, MENTORS_ADJ_FORMS)}.`,
);

// Unique players GM'd: distinct (non-bot) players who joined a counted game
// the user GM'd. d10 — the one remaining unused die in the catalog.
const uniquePlayersGmedBadges = buildProgressionTrack(
    BadgeCategory.UniquePlayersGmed,
    10,
    UNIQUE_PLAYERS_GMED_TITLES,
    UNIQUE_PLAYERS_GMED_TIERS,
    (_tier, threshold) =>
        `Vod(io|ila) igre za barem ${threshold} različitih igrača.`,
);

const specialBadges: SpecialBadgeDefinition[] = [
    {
        key: 'special.megadungeon_winner',
        category: BadgeCategory.Special,
        title: 'Pobjednik megadungeona',
        titleFemale: 'Pobjednica megadungeona',
        description: 'Preživ(io|jela) i osvoj(io|ila) Megadungeon.',
        visual: 'crown',
    },
    {
        key: 'special.ambassador',
        category: BadgeCategory.Special,
        title: 'Ambasador ODG-a',
        titleFemale: 'Ambasadorica ODG-a',
        description: 'Predstavlja(o|la) ODG na vanjskim događanjima (konvencije, festivali).',
        visual: 'star',
    },
    {
        key: 'special.rekorder',
        category: BadgeCategory.Special,
        title: 'Rekorder',
        titleFemale: 'Rekorderka',
        description: 'Sezonsk(i|a) rekorder(|ka) po odabranom kriteriju (npr. najviše vođenih ili odigranih igara). Detalji u bilješci.',
        visual: 'trophy',
    },
];

const PROGRESSION_BADGES: ProgressionBadgeDefinition[] = [
    ...playerBadges,
    ...gmBadges,
    ...systemsPlayedBadges,
    ...systemsGmedBadges,
    ...repeatedGamesGmedBadges,
    ...streakBadges,
    ...mentorsBadges,
    ...uniquePlayersGmedBadges,
];

const ALL_BADGES: BadgeDefinition[] = [
    ...PROGRESSION_BADGES,
    ...specialBadges,
];

export const BADGE_CATALOG: Readonly<Record<string, BadgeDefinition>> = Object.freeze(
    Object.fromEntries(ALL_BADGES.map((badge) => [badge.key, badge])),
);

export function getBadgeDefinition(key: string): BadgeDefinition | undefined {
    return BADGE_CATALOG[key];
}

/**
 * Returns the badge title in the form that matches `gender`. Falls back to
 * the masculine title when no feminine override is defined for the badge.
 */
export function resolveBadgeTitle(badge: BadgeDefinition, gender: Gender): string {
    return pickGender(badge.title, badge.titleFemale, gender);
}

/**
 * Resolves a badge description's inline gender tokens against `gender`.
 * Descriptions without tokens pass through unchanged.
 */
export function resolveBadgeDescription(badge: BadgeDefinition, gender: Gender): string {
    return applyGender(badge.description, gender);
}

export function isProgressionBadge(
    badge: BadgeDefinition,
): badge is ProgressionBadgeDefinition {
    return badge.category !== BadgeCategory.Special;
}

// Visual order of badge chips on Profile. The current-streak flame indicator
// is rendered before this list in UserBadges; chips ranked by die "weight"
// after that, with specials slotted right after the streak badge so the
// rarest awards sit near the front.
//
// Exported as `BADGE_CATEGORY_ORDER` so the MyBadges route (and any other
// future surface) can render tracks in the same order Profile shows them.
export const BADGE_CATEGORY_ORDER: ReadonlyArray<BadgeCategory> = [
    BadgeCategory.Streak,             // flame (longest-streak badge)
    BadgeCategory.Special,            // crown, etc.
    BadgeCategory.RepeatedGamesGmed,  // 2d6
    BadgeCategory.GM,                 // d20
    BadgeCategory.SystemsGmed,        // d12
    BadgeCategory.UniquePlayersGmed,  // d10
    BadgeCategory.SystemsPlayed,      // d8
    BadgeCategory.Player,             // d6
    BadgeCategory.Mentors,            // d4
];

const TIER_ORDER: Record<BadgeTier, number> = {
    [BadgeTier.Wood]: 0,
    [BadgeTier.Bronze]: 1,
    [BadgeTier.Silver]: 2,
    [BadgeTier.Gold]: 3,
};

export function compareBadges(a: BadgeDefinition, b: BadgeDefinition): number {
    const categoryDiff = BADGE_CATEGORY_ORDER.indexOf(a.category) - BADGE_CATEGORY_ORDER.indexOf(b.category);

    if (categoryDiff !== 0) {
        return categoryDiff;
    }

    if (isProgressionBadge(a) && isProgressionBadge(b)) {
        return TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
    }

    return a.title.localeCompare(b.title);
}

function highestTierThresholdMet(
    count: number,
    category: Exclude<BadgeCategory, 'special'>,
    tiers: ReadonlyArray<{ tier: BadgeTier; threshold: number }>,
): string | null {
    let earnedKey: string | null = null;

    for (const { tier, threshold } of tiers) {
        if (count >= threshold) {
            earnedKey = `${category}.${tier}`;
        } else {
            break;
        }
    }

    return earnedKey;
}

/**
 * Returns the badge keys a user is currently eligible for based on aggregate
 * counts. Only the highest-tier badge per progression category is returned —
 * older tiers aren't kept as separate "earned" rows in the UI. The SQL side
 * stores all earned tiers historically; the UI dedupes via this helper.
 */
export function deriveAutoBadgeKeys(counts: BadgeAggregateCounts): string[] {
    const keys: string[] = [];

    const playerKey = highestTierThresholdMet(counts.playerCount, BadgeCategory.Player, GAME_TIERS);
    if (playerKey != null) {
        keys.push(playerKey);
    }

    const gmKey = highestTierThresholdMet(counts.gmCount, BadgeCategory.GM, GAME_TIERS);
    if (gmKey != null) {
        keys.push(gmKey);
    }

    const systemsPlayedKey = highestTierThresholdMet(
        counts.systemsPlayedCount,
        BadgeCategory.SystemsPlayed,
        SYSTEM_TIERS,
    );
    if (systemsPlayedKey != null) {
        keys.push(systemsPlayedKey);
    }

    const systemsGmedKey = highestTierThresholdMet(
        counts.systemsGmedCount,
        BadgeCategory.SystemsGmed,
        SYSTEM_TIERS,
    );
    if (systemsGmedKey != null) {
        keys.push(systemsGmedKey);
    }

    const repeatedGmedKey = highestTierThresholdMet(
        counts.maxRepeatedGmedCount,
        BadgeCategory.RepeatedGamesGmed,
        REPEATED_GAMES_TIERS,
    );
    if (repeatedGmedKey != null) {
        keys.push(repeatedGmedKey);
    }

    const streakKey = highestTierThresholdMet(
        counts.longestStreak,
        BadgeCategory.Streak,
        STREAK_TIERS,
    );
    if (streakKey != null) {
        keys.push(streakKey);
    }

    const mentorsKey = highestTierThresholdMet(
        counts.mentorsCount,
        BadgeCategory.Mentors,
        MENTORS_TIERS,
    );
    if (mentorsKey != null) {
        keys.push(mentorsKey);
    }

    const uniquePlayersGmedKey = highestTierThresholdMet(
        counts.uniquePlayersGmedCount,
        BadgeCategory.UniquePlayersGmed,
        UNIQUE_PLAYERS_GMED_TIERS,
    );
    if (uniquePlayersGmedKey != null) {
        keys.push(uniquePlayersGmedKey);
    }

    return keys;
}

export function getAllSpecialBadgeDefinitions(): SpecialBadgeDefinition[] {
    return specialBadges.slice();
}

/**
 * Returns the live activity count that drives thresholds for a progression
 * category. Mirrors the per-chip count derivation in `useBadgesForUser`,
 * lifted out so the MyBadges page can reach into `BadgeAggregateCounts` for
 * categories the user hasn't earned anything in yet (no badge row to read).
 *
 * Streak badges read `longestStreak` because that's what unlocks tiers;
 * `currentStreak` powers the standalone flame indicator on Profile.
 */
export function countForProgressionCategory(
    category: Exclude<BadgeCategory, 'special'>,
    counts: BadgeAggregateCounts,
): number {
    switch (category) {
        case BadgeCategory.Player:
            return counts.playerCount;
        case BadgeCategory.GM:
            return counts.gmCount;
        case BadgeCategory.SystemsPlayed:
            return counts.systemsPlayedCount;
        case BadgeCategory.SystemsGmed:
            return counts.systemsGmedCount;
        case BadgeCategory.RepeatedGamesGmed:
            return counts.maxRepeatedGmedCount;
        case BadgeCategory.Streak:
            return counts.longestStreak;
        case BadgeCategory.Mentors:
            return counts.mentorsCount;
        case BadgeCategory.UniquePlayersGmed:
            return counts.uniquePlayersGmedCount;
    }
}

/**
 * User-facing label for a badge category, used by the MyBadges route to
 * head each track section. Distinct from the admin-modal subheaders in
 * `awardBadgeOptions.ts` so each surface can tune its tone independently.
 */
export const BADGE_CATEGORY_DISPLAY_LABEL: Record<BadgeCategory, string> = {
    [BadgeCategory.GM]: 'Vođenje igara',
    [BadgeCategory.Player]: 'Odigrane igre',
    [BadgeCategory.SystemsGmed]: 'Sustavi kao GM',
    [BadgeCategory.SystemsPlayed]: 'Iskušani sustavi',
    [BadgeCategory.RepeatedGamesGmed]: 'Ponovljene igre',
    [BadgeCategory.Mentors]: 'Različiti GM-ovi',
    [BadgeCategory.UniquePlayersGmed]: 'Različiti igrači za stolom',
    [BadgeCategory.Streak]: 'Niz uzastopnih evenata',
    [BadgeCategory.Special]: 'Posebne značke',
};

/**
 * Returns every progression badge in a category, sorted by threshold ascending.
 * Used by the "all badges" route to render every tier (earned + locked).
 */
export function getProgressionBadgesForCategory(
    category: Exclude<BadgeCategory, 'special'>,
): ProgressionBadgeDefinition[] {
    return PROGRESSION_BADGES
        .filter((badge) => badge.category === category)
        .slice()
        .sort((a, b) => a.threshold - b.threshold);
}

/**
 * Returns the smallest-threshold progression badge in a category whose
 * threshold strictly exceeds `currentValue`. Returns null at the top of
 * the ladder (i.e. the user has already surpassed every threshold).
 *
 * Used by the streak indicator's explainer to point at the next unearned
 * tier given the user's longest_streak, even when they have no badge from
 * the category yet (where `getNextProgressionBadge` wouldn't help — that
 * function needs an existing badge as anchor).
 */
export function getNextProgressionBadgeForCategory(
    category: Exclude<BadgeCategory, 'special'>,
    currentValue: number,
): ProgressionBadgeDefinition | null {
    let best: ProgressionBadgeDefinition | null = null;

    for (const candidate of PROGRESSION_BADGES) {
        if (candidate.category !== category) {
            continue;
        }
        if (candidate.threshold <= currentValue) {
            continue;
        }
        if (best == null || candidate.threshold < best.threshold) {
            best = candidate;
        }
    }

    return best;
}

/**
 * Finds the next-tier progression badge after a given one (same category,
 * smallest threshold greater than the input). Returns null at the top of
 * the ladder or for special badges.
 */
export function getNextProgressionBadge(
    definition: ProgressionBadgeDefinition,
): ProgressionBadgeDefinition | null {
    let best: ProgressionBadgeDefinition | null = null;

    for (const candidate of PROGRESSION_BADGES) {
        if (candidate.category !== definition.category) {
            continue;
        }
        if (candidate.threshold <= definition.threshold) {
            continue;
        }
        if (best == null || candidate.threshold < best.threshold) {
            best = candidate;
        }
    }

    return best;
}
